import React, { useState, useEffect } from 'react';
import {
    Box,
    Title,
    Text,
    Alert,
    Table,
    Paper,
    Button,
    Badge,
    Group,
} from '@mantine/core';
import { IconRefresh } from '@tabler/icons-react';
import { entityService } from '../../services/entityServiceSingleton';
import { formatDateTime } from '../../utils/dateTimeUtils';
import { TABLE_SCROLL_MIN_WIDTH } from '../../theme/mantineTheme';
import { EmptyState } from '../common/EmptyState';
import { LoadingState } from '../common/LoadingState';

export interface TextIndex {
    Name: string;
    ItemCount: number;
    IndexSizeMB: number;
    MergeStatus: number;
    PopulateCompletionAge: number;
    ImportStatus: number;
    LastPopulated: string;
    PopulateStatus: string;
}

const SearchIndexes: React.FC = () => {
    const [textIndexes, setTextIndexes] = useState<TextIndex[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const fetchTextIndexes = async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await entityService.getTextIndex();
            setTextIndexes(data);
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to fetch text indexes';
            setError(errorMessage);
            console.error('Error fetching text indexes:', err);
            setTextIndexes([]);
        } finally {
            setLoading(false);
        }
    };

    const handleRefresh = async () => {
        await fetchTextIndexes();
    };

    useEffect(() => {
        fetchTextIndexes();
    }, []);

    const getPopulateStatusColor = (status: string) => {
        if (status === 'Idle') return 'green';
        if (status?.includes('Progress')) return 'blue';
        if (status === 'Paused') return 'yellow';
        return 'gray';
    };

    return (
        <Box p="lg">
            <Group justify="space-between" align="center" mb="lg">
                <Title order={4} mb="xs">
                    Search Indexes
                </Title>
                <Button
                    variant="outline"
                    leftSection={<IconRefresh size={18} />}
                    onClick={handleRefresh}
                    disabled={loading}
                >
                    Refresh
                </Button>
            </Group>

            <Text size="sm" c="dimmed" mb="lg">
                View full-text search index information and status.
            </Text>

            {error && (
                <Alert color="red" mb="md">
                    {error}
                </Alert>
            )}

            {loading ? (
                <LoadingState variant="centered" />
            ) : (
                <Paper withBorder p="md">
                    {textIndexes.length === 0 ? (
                        <EmptyState title="No text indexes found" />
                    ) : (
                        <Table.ScrollContainer minWidth={TABLE_SCROLL_MIN_WIDTH}>
                            <Table withTableBorder stickyHeader>
                                <Table.Thead>
                                    <Table.Tr>
                                        <Table.Th><strong>Name</strong></Table.Th>
                                        <Table.Th><strong>Item Count</strong></Table.Th>
                                        <Table.Th><strong>Index Size (MB)</strong></Table.Th>
                                        <Table.Th><strong>Populate Status</strong></Table.Th>
                                        <Table.Th><strong>Last Populated</strong></Table.Th>
                                        <Table.Th><strong>Populate Completion Age</strong></Table.Th>
                                        <Table.Th><strong>Merge Status</strong></Table.Th>
                                        <Table.Th><strong>Import Status</strong></Table.Th>
                                    </Table.Tr>
                                </Table.Thead>
                                <Table.Tbody>
                                    {textIndexes.map((item, index) => (
                                        <Table.Tr key={index}>
                                            <Table.Td>
                                                <Badge variant="outline" color="blue">
                                                    {item.Name || 'N/A'}
                                                </Badge>
                                            </Table.Td>
                                            <Table.Td>
                                                <Text size="sm">
                                                    {item.ItemCount?.toLocaleString() || 'N/A'}
                                                </Text>
                                            </Table.Td>
                                            <Table.Td>
                                                <Text size="sm">
                                                    {item.IndexSizeMB ? `${item.IndexSizeMB.toFixed(2)} MB` : 'N/A'}
                                                </Text>
                                            </Table.Td>
                                            <Table.Td>
                                                <Badge
                                                    variant="outline"
                                                    size="sm"
                                                    color={getPopulateStatusColor(item.PopulateStatus || '')}
                                                >
                                                    {item.PopulateStatus || 'N/A'}
                                                </Badge>
                                            </Table.Td>
                                            <Table.Td>
                                                <Text size="sm" ff="monospace" fz="xs">
                                                    {item.LastPopulated ? formatDateTime(item.LastPopulated) : 'N/A'}
                                                </Text>
                                            </Table.Td>
                                            <Table.Td>
                                                <Text size="sm">
                                                    {item.PopulateCompletionAge !== undefined ? item.PopulateCompletionAge.toLocaleString() : 'N/A'}
                                                </Text>
                                            </Table.Td>
                                            <Table.Td>
                                                <Text size="sm">
                                                    {item.MergeStatus !== undefined ? item.MergeStatus.toString() : 'N/A'}
                                                </Text>
                                            </Table.Td>
                                            <Table.Td>
                                                <Text size="sm">
                                                    {item.ImportStatus !== undefined ? item.ImportStatus.toString() : 'N/A'}
                                                </Text>
                                            </Table.Td>
                                        </Table.Tr>
                                    ))}
                                </Table.Tbody>
                            </Table>
                        </Table.ScrollContainer>
                    )}
                </Paper>
            )}
        </Box>
    );
};

export default SearchIndexes;
